// Attachment chips. In the composer: the picked files with their type icon,
// name (one line, ellipsis), size or upload state, and a 44px Hapus button;
// an error chip names the reason. In the thread: the message's files as
// download links (attachmentDownloadUrl); an image shows a small thumbnail
// only when its bytes can be fetched with the session, else its icon.
import { useEffect, useState } from "react";
import type { AttachmentRef } from "../api";
import * as API from "../api";
import { api } from "../client";
import { Glyph } from "../ui/icons";
import { fileKind, formatBytes, kindGlyph, kindLabel, type AttachmentItem } from "./attachments";

export function ComposerChips({ items, onRemove, disabled = false }: { items: AttachmentItem[]; onRemove: (key: string) => void; disabled?: boolean }) {
  if (items.length === 0) return null;
  return (
    <ul className="att-chips composer-chips" aria-label="Lampiran">
      {items.map((it) => {
        const meta = it.status === "uploading" ? "Mengunggah" : it.status === "error" ? it.error : `${kindLabel(it.name, it.mimeType)}, ${formatBytes(it.size)}`;
        return (
          <li key={it.key} className="att-chip" data-status={it.status} aria-busy={it.status === "uploading" ? true : undefined}>
            <span className="att-chip-icon" aria-hidden="true">
              {it.status === "uploading" ? <svg className="att-spin" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
                  <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="26 12" />
                </svg> : <Glyph name={it.status === "error" ? "danger" : kindGlyph(fileKind(it.name, it.mimeType))} size={20} />}
            </span>
            <span className="att-chip-text">
              <span className="att-chip-name" title={it.name}>
                {it.name}
              </span>
              <span className="att-chip-meta" role={it.status === "error" ? "alert" : undefined}>
                {meta}
              </span>
            </span>
            <button type="button" className="ui-icon-btn att-chip-remove" aria-label={`Hapus ${it.name}`} title="Hapus" disabled={disabled} onClick={() => onRemove(it.key)}>
              <Glyph name="close" size={16} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Thumbnails fetched once per attachment id for the page's life. */
const thumbs = new Map<string, Promise<string | null>>();

/** Fetches an image attachment with the session; null when it cannot be read. */
function loadThumb(id: string): Promise<string | null> {
  let p = thumbs.get(id);
  if (!p) {
    p = (async () => {
      try {
        const res = await api.chat.attachments[":id"].download.$get({ param: { id } });
        if (!res.ok) return null;
        const blob = await (res as unknown as Response).blob();
        if (!blob.type.startsWith("image/") || typeof URL.createObjectURL !== "function") return null;
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    })();
    thumbs.set(id, p);
  }
  return p;
}

function useThumb(a: AttachmentRef, enabled: boolean): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    loadThumb(a.id).then((url) => alive && setSrc(url));
    return () => {
      alive = false;
    };
  }, [a.id, enabled]);
  return src;
}

/** A file that was never stored (the guest's inline file): its chip, no link. */
function LocalChip({ a }: { a: AttachmentRef }) {
  return (
    <span className="att-chip" data-local="">
      <span className="att-chip-icon" aria-hidden="true">
        <Glyph name={kindGlyph(fileKind(a.name, a.mimeType))} size={20} />
      </span>
      <span className="att-chip-text">
        <span className="att-chip-name" title={a.name}>
          {a.name}
        </span>
        <span className="att-chip-meta">
          {kindLabel(a.name, a.mimeType)}, {formatBytes(a.size)}
        </span>
      </span>
    </span>
  );
}

export function AttachmentLink({ a }: { a: AttachmentRef }) {
  const kind = fileKind(a.name, a.mimeType);
  const [broken, setBroken] = useState(false);
  const fetched = useThumb(a, kind === "image" && !!a.id);
  const thumb = broken ? null : fetched;
  if (!a.id) return <LocalChip a={a} />;
  return (
    <a className="att-chip att-link" href={API.attachmentDownloadUrl(a.id)} download={a.name} data-kind={a.kind} title={`Unduh ${a.name}`}>
      <span className="att-chip-icon" data-thumb={thumb ? "" : undefined} aria-hidden="true">
        {thumb ? <img className="att-thumb" src={thumb} alt="" width={36} height={36} onError={() => setBroken(true)} /> : <Glyph name={kindGlyph(kind)} size={20} />}
      </span>
      <span className="att-chip-text">
        <span className="att-chip-name">{a.name}</span>
        <span className="att-chip-meta">
          {kindLabel(a.name, a.mimeType)}, {formatBytes(a.size)}
        </span>
      </span>
      <span className="att-chip-end" aria-hidden="true">
        <Glyph name="documentDownload" size={16} />
      </span>
    </a>
  );
}

export function MessageAttachments({ items, label = "Lampiran" }: { items: AttachmentRef[]; label?: string }) {
  if (items.length === 0) return null;
  return (
    <ul className="att-chips msg-attachments" aria-label={label}>
      {items.map((a) => (
        <li key={a.id}>
          <AttachmentLink a={a} />
        </li>
      ))}
    </ul>
  );
}
